/*
 * Pricing card behaviour: the quantity steppers, the delivery countdown, and the
 * Delivery Timer app's estimate.
 *
 * The steppers and the countdown are drawn on phones only and the app's estimate
 * at both widths, but none of it is gated on the viewport here — visibility is
 * CSS's business, so there is no state to tear down on the way across the
 * breakpoint and nothing to re-measure on resize.
 *
 * The scented/unscented switch is deliberately not in this file: it is a real
 * radio group switched by CSS sibling selectors, so it keeps working with the
 * script blocked and stays arrow-key navigable for free.
 */
class StandalonePricing extends HTMLElement {
  /* Identifies this section's own add requests to the skip rule below. Set once
     for the class, since two of these sections on a page share the one rule. */
  static ADD_MARKER = 'standalone_pricing_add=1';
  static claimed = false;

  connectedCallback() {
    this.steppers = Array.from(this.querySelectorAll('[data-price-qty]'));
    this.countdowns = Array.from(this.querySelectorAll('[data-price-countdown]'));
    this.deliveries = Array.from(this.querySelectorAll('[data-price-delivery]'));

    this.onStepperClick = (event) => this.handleStep(event);
    this.onStepperChange = (event) => this.handleChange(event);
    this.onCtaClick = (event) => this.handleAdd(event);
    this.addEventListener('click', this.onCtaClick);
    // Delegated as well, and separately: the tiles are printed once per card and
    // never replaced, so there is nothing per-node to bind or to tear down.
    this.onThumbClick = (event) => this.handleThumb(event);
    this.addEventListener('click', this.onThumbClick);

    this.claimAddsFromCartDrawerApp();

    this.steppers.forEach((stepper) => {
      stepper.addEventListener('click', this.onStepperClick);
      stepper.addEventListener('change', this.onStepperChange);
      // Runs once up front so the minus button starts disabled at the floor and
      // the cart link starts carrying whatever quantity the markup shipped with.
      this.commit(stepper, this.read(stepper));
    });

    if (this.countdowns.length > 0) {
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    if (this.deliveries.length > 0) this.loadDelivery();
  }

  disconnectedCallback() {
    this.steppers.forEach((stepper) => {
      stepper.removeEventListener('click', this.onStepperClick);
      stepper.removeEventListener('change', this.onStepperChange);
    });

    this.removeEventListener('click', this.onCtaClick);
    this.removeEventListener('click', this.onThumbClick);

    if (this.timer) clearInterval(this.timer);

    // The theme editor tears the section down and rebuilds it on every settings
    // change, so an in-flight request has to die with it rather than come back
    // and write into a node that has already been thrown away.
    if (this.deliveryAbort) this.deliveryAbort.abort();
  }

  /* ----------------------------------------------------------------- cart */

  /* The storefront runs UpCart, which patches window.fetch and XMLHttpRequest
     and answers any add it sees with its own drawer. It exposes one honest way
     out: upcartShouldSkipAddToCart, which it consults per request and which
     makes it ignore that request completely — no drawer, no cart refetch.
     Nothing else reliably suppresses it. Query flags do not, because it only
     reads opens_cart when upcart=1 is present too, and a plain <form> POST does
     not either, because it cancels the submit and reissues the add itself.

     Composed rather than assigned, so a rule the merchant or another section
     has already installed keeps working alongside this one. */
  claimAddsFromCartDrawerApp() {
    if (StandalonePricing.claimed) return;
    StandalonePricing.claimed = true;

    const previous = window.upcartShouldSkipAddToCart;
    window.upcartShouldSkipAddToCart = (url) => {
      if (String(url).includes(StandalonePricing.ADD_MARKER)) return true;
      return typeof previous === 'function' ? previous(url) : false;
    };
  }

  /* The button stays a real cart permalink in the markup, so it still adds the
     line with this script blocked and still reads as a link. Where the script
     runs, the click adds the line and goes straight to checkout — no drawer is
     opened by us or by anyone else. Every failure path falls back to following
     the href the markup already carries, which lands on the cart with the
     reason shown. */
  handleAdd(event) {
    const link = event.target.closest('[data-price-cta]');
    if (!link) return;

    // No variant means the button points at a page rather than the cart.
    const variantId = link.dataset.priceVariant;
    if (!variantId) return;

    event.preventDefault();
    // Left set on the way out: the page is navigating, and a second click in
    // the meantime would add the bundle twice.
    if (link.getAttribute('aria-disabled') === 'true') return;
    link.setAttribute('aria-disabled', 'true');

    const card = link.closest('[data-price-card]');
    const stepper = card ? card.querySelector('[data-price-qty]') : null;

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', String(stepper ? this.read(stepper) : 1));

    // The marker is what the skip rule above matches on. Shopify ignores query
    // parameters it does not know, so it costs the add nothing. No sections are
    // requested either: nothing here is re-rendered before the redirect.
    const base = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const addUrl = `${base}?${StandalonePricing.ADD_MARKER}`;

    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    })
      .then((response) => response.json())
      .then((response) => {
        // A refused line — sold out, or past what the stock allows — is the cart
        // answering, not the request failing. The permalink is the honest
        // fallback there: it puts the visitor in front of the same refusal
        // written out in full, rather than swallowing it on a silent button.
        if (response.status) {
          window.location = link.getAttribute('href');
          return;
        }

        window.location = this.checkoutUrl();
      })
      .catch(() => {
        link.removeAttribute('aria-disabled');
        window.location = link.getAttribute('href');
      });
  }

  /* Built from the theme's own cart_url rather than a bare '/checkout', so a
     storefront with a locale prefix (/en-ca/cart, /fr/cart) checks out under
     that same prefix instead of dropping it. */
  checkoutUrl() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return cartUrl.replace(/\/cart\/?$/, '/checkout');
  }

  /* -------------------------------------------------------------- gallery */

  /* A tile swaps the picture in the band above it and touches nothing else on
     the card. Scoped to the tile's own card, so the two cards on a page keep
     separate galleries. */
  handleThumb(event) {
    const button = event.target.closest('[data-price-thumb]');
    if (!button) return;

    const card = button.closest('[data-price-card]');
    const media = card && card.querySelector('[data-price-media]');
    if (!media) return;

    this.paintBand(media, button);

    // The ring is drawn on the <li>, not the button: it is an overlay across the
    // whole tile including the 1.5px it sits on. Every tile in the row is
    // rewritten rather than just the two that change, so a row that somehow
    // shipped with two marked settles onto one.
    const row = button.closest('.pricing__thumbs');
    if (!row) return;

    row.querySelectorAll('[data-price-thumb]').forEach((other) => {
      const on = other === button;
      other.setAttribute('aria-pressed', String(on));
      const tile = other.closest('.pricing__thumb');
      if (tile) tile.classList.toggle('pricing__thumb--on', on);
    });
  }

  /* Writes the tile's pair of sources into the band's existing nodes rather than
     rebuilding it, so whatever the band was — one picture or a split pair — is
     what it stays, and the sizes the markup worked out stay with it. */
  paintBand(media, button) {
    const data = button.dataset;
    let img = media.querySelector('img');

    // A card with no band picture at all draws a placeholder div, which has
    // nothing to write into. The tile still has to work, so the div is replaced
    // by an <img> carrying the band's own sizes — the same one image_tag prints
    // in the single-picture branch.
    if (!img) {
      const placeholder = media.querySelector('.pricing__placeholder');
      if (!placeholder) return;
      img = document.createElement('img');
      img.setAttribute('sizes', '(min-width: 900px) 604px, calc(100vw - 32px)');
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
      placeholder.replaceWith(img);
    }

    this.holdBandFrame(media, img);

    // Two nodes only where the band was printed with two: the <source> is the
    // desktop half and the <img> is the phone one. A band with no source is the
    // single-picture branch, whose img spans both widths on its own.
    const source = media.querySelector('picture source');

    if (source) {
      source.srcset = data.thumbSrcset;
      img.srcset = data.thumbSrcsetM;
      img.src = data.thumbSrcM;
    } else {
      img.srcset = data.thumbSrcset;
      img.src = data.thumbSrc;
    }

    img.alt = data.thumbAlt || '';
  }

  /* Auto height hands the band's height to whatever picture it is showing, which
     is right for the one the card is built around and wrong the moment a tile can
     replace it: a squarer shot would grow the band, and everything under it —
     bullets, price, button — would jump down the page on a tap.

     So the first swap pins the band to the ratio the hero already drew and lets
     the pictures after it crop into that frame instead. Inline, because it is
     measured rather than authored, and a ratio rather than a height so the frame
     stays fluid. Fixed-height bands never come in here at all: the frame is
     already the drawn 200/285 and the picture already covers it. */
  holdBandFrame(media, img) {
    if (!media.classList.contains('pricing__media--auto')) return;
    if (media.style.aspectRatio) return;

    // The frame as drawn, not the picture's own pixels. It is the box that must
    // not move, and reading it back is exact whatever produced it — a picture
    // that has not decoded yet reports nothing to pin and is left alone, which
    // is the one case where the band is not showing a height worth keeping.
    const frame = media.getBoundingClientRect();
    const width = frame.width;
    const height = frame.height;
    if (!width || !height) return;

    media.style.aspectRatio = `${width} / ${height}`;
    // Beats `.pricing__media--auto img { height: auto }`, which would otherwise
    // let the new picture's own ratio decide the height all over again.
    img.style.height = '100%';
    img.style.objectFit = 'contain';
  }

  /* ------------------------------------------------------------- quantity */

  read(stepper) {
    const input = stepper.querySelector('[data-price-qty-input]');
    const value = input ? parseInt(input.value, 10) : NaN;
    return Number.isNaN(value) ? this.min(stepper) : value;
  }

  min(stepper) {
    return Number(stepper.dataset.qtyMin) || 1;
  }

  max(stepper) {
    return Number(stepper.dataset.qtyMax) || 99;
  }

  handleStep(event) {
    const button = event.target.closest('[data-price-qty-step]');
    if (!button) return;

    const stepper = event.currentTarget;
    this.commit(stepper, this.read(stepper) + Number(button.dataset.priceQtyStep));
  }

  handleChange(event) {
    if (!event.target.matches('[data-price-qty-input]')) return;

    const stepper = event.currentTarget;
    this.commit(stepper, this.read(stepper));
  }

  commit(stepper, value) {
    const min = this.min(stepper);
    const max = this.max(stepper);
    const next = Math.min(Math.max(value, min), max);

    const input = stepper.querySelector('[data-price-qty-input]');
    if (input) input.value = String(next);

    stepper.querySelectorAll('[data-price-qty-step]').forEach((button) => {
      const step = Number(button.dataset.priceQtyStep);
      button.disabled = step < 0 ? next <= min : next >= max;
    });

    this.syncLink(stepper, next);
  }

  /* The stepper is only honestly "functional" if it changes what the button
     does. With a variant id set the link is a cart permalink and the quantity
     is the part after the colon, which is a real add-to-cart. Without one there
     is no variant to add, so the count is passed along as a query parameter for
     whatever the link points at to read. */
  syncLink(stepper, value) {
    const card = stepper.closest('[data-price-card]');
    const link = card ? card.querySelector('[data-price-cta]') : null;
    if (!link) return;

    const permalink = link.dataset.priceCartBase;
    if (permalink) {
      link.setAttribute('href', `${permalink}:${value}`);
      return;
    }

    const base = link.dataset.priceHref;
    if (!base || base === '#') return;

    try {
      const url = new URL(base, window.location.href);
      url.searchParams.set('quantity', String(value));
      link.setAttribute('href', url.toString());
    } catch (error) {
      // A hand-typed link that is not a URL at all is left exactly as authored.
    }
  }

  /* ------------------------------------------------------------ countdown */

  /* Both halves of the sentence are derived from the same deadline, so the
     promised date and the time left can never disagree: orders placed before
     today's cutoff ship against today, after it against tomorrow. Recomputed
     every tick rather than once, so the line rolls over on its own when the
     clock passes the cutoff with the page still open. */
  tick() {
    const now = new Date();

    this.countdowns.forEach((widget) => {
      const cutoffHour = Number(widget.dataset.cutoffHour) || 0;
      const leadDays = Number(widget.dataset.leadDays) || 0;

      const deadline = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoffHour);
      if (deadline <= now) deadline.setDate(deadline.getDate() + 1);

      const delivery = new Date(
        deadline.getFullYear(),
        deadline.getMonth(),
        deadline.getDate() + leadDays
      );

      const dateNode = widget.querySelector('[data-price-countdown-date]');
      const timeNode = widget.querySelector('[data-price-countdown-time]');

      if (dateNode) {
        dateNode.textContent = delivery.toLocaleDateString(undefined, {
          month: 'short',
          day: '2-digit',
        });
      }

      if (timeNode) timeNode.textContent = this.formatRemaining(deadline - now);

      // Hidden in the markup so the half-written sentence never shows to a
      // visitor whose script never arrived.
      widget.hidden = false;
    });
  }

  formatRemaining(milliseconds) {
    const total = Math.max(0, Math.floor(milliseconds / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    return `${hours}hr ${minutes}mins ${seconds}sec`;
  }

  /* ------------------------------------------------------------- delivery */

  /* The Delivery Timer app's line and its location picker, rebuilt from the app's
     own backend. Its embed cannot do it here — the handle comes from the last URL
     segment and the fetch is gated on the page type being product — so nothing of
     the app renders on a page template. Both endpoints answer with
     Access-Control-Allow-Origin: *, so the browser can ask them the same two
     questions the embed asks.

     Nothing about the sentence is reimplemented: message_on_front_end is the
     app's own HTML and des_delivery_lead_date its finished range. Only the two
     placeholders the app fills are filled here, with the same markup it uses.

     The endpoints are undocumented, so every failure is silent by design: the
     node ships hidden and simply never appears, leaving whatever else the card
     was already showing. */
  static DT_ORIGIN = 'https://deliverytimer.herokuapp.com';

  /* The app's own bundled country names, copied verbatim out of
     global_deliverytimer.min.js. The geolocation endpoint answers with an empty
     country_name and the app fills it from this list — which is how the picker
     titles itself "(India)" for a country that is not even a configured zone.
     Copied rather than read off Intl.DisplayNames, which disagrees on four of the
     242 (Turkey, Hong Kong SAR, Macao SAR, Netherlands Antilles). */
  static DT_COUNTRY_NAMES = {"AF":"Afghanistan","AX":"Åland Islands","AL":"Albania","DZ":"Algeria","AD":"Andorra","AO":"Angola","AI":"Anguilla","AG":"Antigua & Barbuda","AR":"Argentina","AM":"Armenia","AW":"Aruba","AU":"Australia","AT":"Austria","AZ":"Azerbaijan","BS":"Bahamas","BH":"Bahrain","BD":"Bangladesh","BB":"Barbados","BY":"Belarus","BE":"Belgium","BZ":"Belize","BJ":"Benin","BM":"Bermuda","BT":"Bhutan","BO":"Bolivia","BA":"Bosnia & Herzegovina","BW":"Botswana","BV":"Bouvet Island","BR":"Brazil","IO":"British Indian Ocean Territory","VG":"British Virgin Islands","BN":"Brunei","BG":"Bulgaria","BF":"Burkina Faso","BI":"Burundi","KH":"Cambodia","CM":"Cameroon","CA":"Canada","CV":"Cape Verde","BQ":"Caribbean Netherlands","KY":"Cayman Islands","CF":"Central African Republic","TD":"Chad","CL":"Chile","CN":"China","CX":"Christmas Island","CC":"Cocos (Keeling) Islands","CO":"Colombia","KM":"Comoros","CG":"Congo - Brazzaville","CD":"Congo - Kinshasa","CK":"Cook Islands","CR":"Costa Rica","HR":"Croatia","CU":"Cuba","CW":"Curaçao","CY":"Cyprus","CZ":"Czechia","CI":"Côte d’Ivoire","DK":"Denmark","DJ":"Djibouti","DM":"Dominica","DO":"Dominican Republic","EC":"Ecuador","EG":"Egypt","SV":"El Salvador","GQ":"Equatorial Guinea","ER":"Eritrea","EE":"Estonia","SZ":"Eswatini","ET":"Ethiopia","FK":"Falkland Islands","FO":"Faroe Islands","FJ":"Fiji","FI":"Finland","FR":"France","GF":"French Guiana","PF":"French Polynesia","TF":"French Southern Territories","GA":"Gabon","GM":"Gambia","GE":"Georgia","DE":"Germany","GH":"Ghana","GI":"Gibraltar","GR":"Greece","GL":"Greenland","GD":"Grenada","GP":"Guadeloupe","GT":"Guatemala","GG":"Guernsey","GN":"Guinea","GW":"Guinea-Bissau","GY":"Guyana","HT":"Haiti","HM":"Heard & McDonald Islands","HN":"Honduras","HK":"Hong Kong SAR","HU":"Hungary","IS":"Iceland","IN":"India","ID":"Indonesia","IR":"Iran","IQ":"Iraq","IE":"Ireland","IM":"Isle of Man","IL":"Israel","IT":"Italy","JM":"Jamaica","JP":"Japan","JE":"Jersey","JO":"Jordan","KZ":"Kazakhstan","KE":"Kenya","KI":"Kiribati","XK":"Kosovo","KW":"Kuwait","KG":"Kyrgyzstan","LA":"Laos","LV":"Latvia","LB":"Lebanon","LS":"Lesotho","LR":"Liberia","LY":"Libya","LI":"Liechtenstein","LT":"Lithuania","LU":"Luxembourg","MO":"Macao SAR","MG":"Madagascar","MW":"Malawi","MY":"Malaysia","MV":"Maldives","ML":"Mali","MT":"Malta","MQ":"Martinique","MR":"Mauritania","MU":"Mauritius","YT":"Mayotte","MX":"Mexico","MD":"Moldova","MC":"Monaco","MN":"Mongolia","ME":"Montenegro","MS":"Montserrat","MA":"Morocco","MZ":"Mozambique","MM":"Myanmar (Burma)","NA":"Namibia","NR":"Nauru","NP":"Nepal","NL":"Netherlands","AN":"Netherlands Antilles","NC":"New Caledonia","NZ":"New Zealand","NI":"Nicaragua","NE":"Niger","NG":"Nigeria","NU":"Niue","NF":"Norfolk Island","KP":"North Korea","MK":"North Macedonia","NO":"Norway","OM":"Oman","PK":"Pakistan","PS":"Palestinian Territories","PA":"Panama","PG":"Papua New Guinea","PY":"Paraguay","PE":"Peru","PH":"Philippines","PN":"Pitcairn Islands","PL":"Poland","PT":"Portugal","QA":"Qatar","RE":"Réunion","RO":"Romania","RU":"Russia","RW":"Rwanda","WS":"Samoa","SM":"San Marino","ST":"São Tomé & Príncipe","SA":"Saudi Arabia","SN":"Senegal","RS":"Serbia","SC":"Seychelles","SL":"Sierra Leone","SG":"Singapore","SX":"Sint Maarten","SK":"Slovakia","SI":"Slovenia","SB":"Solomon Islands","SO":"Somalia","ZA":"South Africa","GS":"South Georgia & South Sandwich Islands","KR":"South Korea","SS":"South Sudan","ES":"Spain","LK":"Sri Lanka","BL":"St. Barthélemy","SH":"St. Helena","KN":"St. Kitts & Nevis","LC":"St. Lucia","MF":"St. Martin","PM":"St. Pierre & Miquelon","VC":"St. Vincent & Grenadines","SD":"Sudan","SR":"Suriname","SJ":"Svalbard & Jan Mayen","SE":"Sweden","CH":"Switzerland","SY":"Syria","TW":"Taiwan","TJ":"Tajikistan","TZ":"Tanzania","TH":"Thailand","TL":"Timor-Leste","TG":"Togo","TK":"Tokelau","TO":"Tonga","TT":"Trinidad & Tobago","TN":"Tunisia","TR":"Turkey","TM":"Turkmenistan","TC":"Turks & Caicos Islands","TV":"Tuvalu","UM":"U.S. Outlying Islands","UG":"Uganda","UA":"Ukraine","AE":"United Arab Emirates","GB":"United Kingdom","US":"United States","UY":"Uruguay","UZ":"Uzbekistan","VU":"Vanuatu","VA":"Vatican City","VE":"Venezuela","VN":"Vietnam","WF":"Wallis & Futuna","EH":"Western Sahara","YE":"Yemen","ZM":"Zambia","ZW":"Zimbabwe"};

  /* The app's own storage keys, deliberately: a location picked on a product page
     is already sitting in here, so the card opens showing that one, and a
     location picked here is what the product page reads next. */
  static DT_KEYS = {
    country: 'dlt_country_code',
    countryName: 'dlt_country_name',
    provinceCode: 'dlt_provinces_code',
    provinceName: 'dlt_provinces_name',
  };

  async loadDelivery() {
    const shop = this.deliveries[0].dataset.dtShop;
    if (!shop) return;

    this.deliveryShop = shop;
    this.deliveryAbort = new AbortController();

    try {
      const geo = await this.fetchDeliveryJson(
        `${StandalonePricing.DT_ORIGIN}/output/get_geolocation_info.json?shop=${encodeURIComponent(shop)}`,
        this.deliveryAbort.signal
      );

      const found = (geo && geo.geo_data) || {};
      // The app's own fallbacks for a lookup that comes back unsure.
      const code =
        found.country_code && found.country_code !== 'Not found' ? found.country_code : 'US';

      this.deliveryGeo = {
        code,
        name: found.country_name || StandalonePricing.DT_COUNTRY_NAMES[code] || code,
        province: found.state || '',
      };
    } catch (error) {
      // No zone to ask about, so no line and no picker.
      return;
    }

    this.onDeliveryClick = (event) => {
      if (event.target.closest('[data-price-delivery-country]')) this.openDeliveryPicker();
    };
    this.addEventListener('click', this.onDeliveryClick);

    await this.showDelivery();
  }

  async fetchDeliveryJson(url, signal) {
    const response = await fetch(url, { signal, credentials: 'omit' });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  }

  /* Whatever location the visitor picked, here or on a product page. */
  deliveryChoice() {
    const keys = StandalonePricing.DT_KEYS;

    try {
      return {
        code: localStorage.getItem(keys.country) || '',
        name: localStorage.getItem(keys.countryName) || '',
        province: localStorage.getItem(keys.provinceName) || '',
      };
    } catch (error) {
      // Locked-down privacy settings throw on the read itself.
      return { code: '', name: '', province: '' };
    }
  }

  /* One request per distinct product rather than per card: the answer is
     zone-level — verified identical with and without a product — so cards sharing
     a product, and the cards with no product at all, share the one response. */
  async showDelivery() {
    const choice = this.deliveryChoice();
    const country = choice.code || this.deliveryGeo.code;
    /* The endpoint takes the province by name, which is what both the lookup and
       the picker hand over. A country chosen without a region carries no province
       at all, rather than the previous zone's. */
    const province = choice.code ? choice.province : this.deliveryGeo.province;

    const pending = new Map();

    await Promise.all(
      this.deliveries.map(async (node) => {
        const productId = node.dataset.dtProduct || '';
        const variantId = node.dataset.dtVariant || '';
        const key = `${productId}|${variantId}`;

        if (!pending.has(key)) {
          const url = new URL(`${StandalonePricing.DT_ORIGIN}/output/view.json`);
          url.searchParams.set('shop', this.deliveryShop);
          url.searchParams.set('country_code', country.toUpperCase());
          url.searchParams.set('province', province);
          // Sent when the card has them so per-product rules keep working if they
          // are ever switched on; ignored while rule_based_status is off.
          if (productId) url.searchParams.set('product_id', productId);
          if (variantId) url.searchParams.set('variant_id', variantId);

          pending.set(key, this.fetchDeliveryJson(url.toString(), this.deliveryAbort.signal));
        }

        try {
          this.paintDelivery(node, await pending.get(key), country, choice);
        } catch (error) {
          // One card's request failing leaves that card's line hidden and the
          // others alone.
        }
      })
    );
  }

  paintDelivery(node, payload, country, choice) {
    const settings = payload && payload.settings;
    if (!settings || settings.dt_status !== true) return;

    const global = payload.global_setting || {};
    if (global.hide_estimate_deliverydate === true) return;
    if (settings.hide_estimate_deliverydate === true) return;

    const message = settings.message_on_front_end;
    const date = settings.des_delivery_lead_date;
    // A zone with no configured date answers with the settings and no range;
    // half the sentence is worse than none of it.
    if (!message || !date) return;
    // The message is authored in the app's own admin, but it is still third party
    // HTML going into innerHTML, so a script tag ends the attempt.
    if (/<\s*script/i.test(message)) return;

    /* The picker offers the merchant's configured zones, not a world list — the
       app overwrites its own bundled list with exactly these two. */
    this.deliveryCountries = payload.configured_countries || {};
    this.deliveryRegions = payload.configured_zones_of_countries || {};
    this.deliveryZones = payload.enbled_zones_count || 0;
    this.deliveryShowName = global.show_country_name === true;

    // Written the app's way, blank colour and all, so the markup matches what a
    // product page produces character for character.
    const dateHtml = `<span class="delivery_date" style="color: ${settings.ds_deliverydate_font_color || ''}">${this.escapeDelivery(date)}</span>`;

    // The <b> around the date lives in the app's message, not here — the app
    // substitutes the bare span, so adding another would double the markup.
    node.innerHTML = message
      .replace(/\{\{\s*country\s*\}\}/gi, this.deliveryCountryLabel(country, choice))
      .replace(/\{\{\s*deliverydate\s*\}\}/i, dateHtml);

    node.hidden = false;
  }

  /* The app's label markup: a flagcdn SVG at 16x12, the country name only when
     the app is set to show it, and the caret that says the thing can be clicked.

     The app hangs this off id="dlt_cname_label". This uses a data attribute
     instead, because the app's script is an embed that loads on every page of the
     storefront, this one included, and registers a document-wide click handler on
     that id — reusing it would open a second popup of the app's own on top of
     ours, built from its bundled world list. Same reason the popup's own classes
     are prefixed.

     A message that carries a literal flag emoji instead of the placeholder — the
     way each configured zone's message is written — gets no label and no picker,
     exactly as on a product page. */
  deliveryCountryLabel(country, choice) {
    // The app hides the label outright when the merchant has no zones enabled.
    if (this.deliveryZones < 1) return '';

    const code = String(country || '').toLowerCase();
    const name = choice.name || this.deliveryGeo.name;
    const flag = `<img src="https://flagcdn.com/${code}.svg" width="16" height="12" alt="${this.escapeDelivery(name)}" style="border-radius: 2px; width:16px;">`;
    const label = this.deliveryShowName ? `&nbsp;<b>${this.escapeDelivery(name)}</b>` : '';
    const caret =
      "<i style='transform: rotate(45deg);-webkit-transform: rotate(45deg);border: solid black;border-width: 0 1.5px 1.5px 0;display: inline-block;padding: 2px;margin-bottom: 3px;margin-left: 4px;'></i>";

    return `<span data-price-delivery-country style='cursor: pointer;display: inline-flex;align-items: center;line-height: 1;font-size: 12px;'>${flag}${label}${caret}</span>`;
  }

  /* --------------------------------------------------------- location picker */

  /* Built to the app's own structure, strings and measurements — the title, the
     search box, the flag rows, the "regions" toggles — with the list assembled
     through the DOM rather than string concatenation, because the app's version
     drops unquoted country names into attributes and a name like Côte d'Ivoire
     would tear its own markup in half. */
  openDeliveryPicker() {
    if (document.querySelector('.pdt-popup-wrapper')) return;

    const geo = this.deliveryGeo;
    const choice = this.deliveryChoice();
    const code = choice.code || geo.code;
    const name = choice.name || geo.name;

    const wrapper = document.createElement('div');
    wrapper.className = 'pdt-popup-wrapper';
    wrapper.innerHTML = `
      <div class="pdt-custom-popup">
        <div class="pdt-popup">
          <div class="pdt-popup-title">
            <span>Select your location -</span>&nbsp;<span class="pdt-country-preview">${this.deliveryFlag(geo.code, 'pdt-flag-icon')}(${this.escapeDelivery(geo.name)})</span>
          </div>
          <div class="pdt-popup-dropdown">
            <div class="pdt-dropdown-container">
              <div class="pdt-dropdown-header">
                ${this.deliveryFlag(code, 'pdt-flag')}
                <span>${this.escapeDelivery(choice.province ? `${name} - ${choice.province}` : name)}</span>
                <i class="arrow"></i>
              </div>
            </div>
            <div class="pdt-dropdown-body">
              <div class="pdt-search">
                <input type="text" placeholder="Search">
                <ul class="pdt-list"><div class="none">No result found</div></ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    const list = wrapper.querySelector('.pdt-list');
    const empty = wrapper.querySelector('.pdt-list > div');

    Object.keys(this.deliveryCountries).forEach((countryCode) => {
      const countryName = this.deliveryCountries[countryCode];
      const regions = this.deliveryRegions[countryCode.toLowerCase()];

      const row = document.createElement('li');
      row.className = 'pdt-country';
      row.dataset.code = countryCode;
      row.dataset.value = countryName;

      if (regions) {
        // Two columns, so the "regions" toggle sits at the far edge.
        row.style.justifyContent = 'space-between';

        const cell = document.createElement('span');
        cell.style.display = 'flex';
        cell.style.alignItems = 'center';
        cell.innerHTML = this.deliveryFlag(countryCode, 'pdt-flag', 'margin-right: 6px');
        cell.append(countryName);

        const toggle = document.createElement('span');
        toggle.className = 'pdt-regions-toggle';
        toggle.style.paddingRight = '13px';
        toggle.textContent = 'regions';

        row.append(cell, toggle);
      } else {
        row.innerHTML = this.deliveryFlag(countryCode, 'pdt-flag', 'margin-right: 6px');
        row.append(countryName);
      }

      list.append(row);

      if (!regions) return;

      Object.keys(regions).forEach((regionCode) => {
        const region = document.createElement('label');
        region.className = 'pdt-region';
        region.dataset.country = countryCode;
        region.dataset.code = regionCode;
        region.textContent = regions[regionCode];
        list.append(region);
      });
    });

    this.wireDeliveryPicker(wrapper, list, empty);
    document.body.append(wrapper);
  }

  wireDeliveryPicker(wrapper, list, empty) {
    const header = wrapper.querySelector('.pdt-dropdown-header');
    const headerLabel = header.querySelector('span');
    const body = wrapper.querySelector('.pdt-dropdown-body');
    const search = wrapper.querySelector('.pdt-search input');

    header.addEventListener('click', () => {
      search.value = '';
      list.querySelectorAll('li').forEach((row) => {
        row.style.display = '';
      });
      empty.style.display = '';
      body.style.display = body.style.display === 'block' ? 'none' : 'block';
    });

    search.addEventListener('keyup', () => {
      const term = search.value.toLowerCase();
      list.querySelectorAll('li').forEach((row) => {
        row.style.display = row.textContent.toLowerCase().includes(term) ? '' : 'none';
      });
    });

    wrapper.addEventListener('click', (event) => {
      // Anywhere outside the card itself closes it, the app's own behaviour.
      if (!event.target.closest('.pdt-popup')) {
        wrapper.remove();
        return;
      }

      // Back to whatever the lookup thinks, by dropping the override entirely.
      if (event.target.closest('.pdt-country-preview')) {
        this.forgetDeliveryChoice();
        wrapper.remove();
        this.showDelivery();
        return;
      }

      const toggle = event.target.closest('.pdt-regions-toggle');
      if (toggle) {
        // One country's regions at a time, and without also picking that country.
        event.stopPropagation();
        const row = toggle.closest('li');
        const open = toggle.classList.contains('active');

        list.querySelectorAll('.pdt-region').forEach((region) => {
          region.style.display = 'none';
        });
        list.querySelectorAll('.pdt-regions-toggle').forEach((other) => {
          other.classList.remove('active');
        });

        if (!open) {
          toggle.classList.add('active');
          list.querySelectorAll(`.pdt-region[data-country="${row.dataset.code}"]`).forEach((region) => {
            region.style.display = 'block';
          });
        }
        return;
      }

      const region = event.target.closest('.pdt-region');
      if (region) {
        const country = region.dataset.country;
        this.rememberDeliveryChoice({
          code: country,
          name: this.deliveryCountries[country],
          provinceCode: region.dataset.code,
          provinceName: region.textContent,
        });
        wrapper.remove();
        this.showDelivery();
        return;
      }

      const row = event.target.closest('li.pdt-country');
      if (row) {
        // The header follows the click before the popup goes, exactly as the
        // app's does.
        list.querySelectorAll('li').forEach((other) => other.classList.remove('active'));
        row.classList.add('active');
        headerLabel.textContent = row.dataset.value;
        headerLabel.classList.add('fill');

        this.rememberDeliveryChoice({ code: row.dataset.code, name: row.dataset.value });
        wrapper.remove();
        this.showDelivery();
      }
    });
  }

  rememberDeliveryChoice({ code, name, provinceCode, provinceName }) {
    const keys = StandalonePricing.DT_KEYS;

    try {
      localStorage.setItem(keys.country, code);
      localStorage.setItem(keys.countryName, name);

      // A country picked on its own clears the region, so the next request does
      // not carry a province belonging to a different country.
      if (provinceName) {
        localStorage.setItem(keys.provinceCode, provinceCode);
        localStorage.setItem(keys.provinceName, provinceName);
      } else {
        localStorage.removeItem(keys.provinceCode);
        localStorage.removeItem(keys.provinceName);
      }
    } catch (error) {
      // The choice then lasts until the next paint, which is still better than
      // refusing to answer the click.
    }
  }

  forgetDeliveryChoice() {
    try {
      Object.values(StandalonePricing.DT_KEYS).forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      // Nothing to forget if it could never be written.
    }
  }

  deliveryFlag(code, className, style) {
    const file = String(code || '').toLowerCase();

    return `<img class="${className}" src="https://flagcdn.com/${file}.svg" width="16" height="12"${style ? ` style="${style}"` : ''}>`;
  }

  escapeDelivery(value) {
    return String(value).replace(/[&<>"]/g, (character) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[character];
    });
  }
}

/* Guarded because two of these sections on one page render the script tag
   twice, and a second define() on the same name throws. */
if (!customElements.get('standalone-pricing')) {
  customElements.define('standalone-pricing', StandalonePricing);
}
